// Shared helper so both AI surfaces — the floating chat box (AssistantPanel) and
// the full-page AI Search (Employee Management) — send Dana the same "where am I"
// context for context-aware navigation help.

// Map the current app-relative route to a human page label. Longest-prefix wins
// (so /payroll/loans beats /payroll).
const PAGE_LABELS: [string, string][] = [
  ['admin/ai-assistant', 'AI Assistant settings'],
  ['admin/import', 'Import Employees'],
  ['admin/departments', 'Departments'],
  ['admin/job-titles', 'Job Titles'],
  ['admin/leave-policies', 'Leave Policies'],
  ['admin/users', 'Users & Roles'],
  ['admin/setup', 'Set up your company'],
  ['admin', 'Administration'],
  ['payroll/loans', 'Loans & Advances'],
  ['payroll/salaries', 'Bulk Salaries'],
  ['payroll/runs', 'Payroll Run'],
  ['payroll', 'Payroll Runs'],
  ['time-off', 'Time Off'],
  ['my-pay', 'My Pay'],
  ['my-team', 'My Team'],
  ['leave-approvals', 'Leave Approvals'],
  ['team-reports', 'Team Reports'],
  ['employees', 'Employee Directory'],
  ['documents', 'Documents'],
  ['personal', 'My Profile'],
  ['settings', 'Company Settings'],
  ['help', 'Help'],
];

export interface DerivedContext {
  path: string;
  page: string;
  /** The specific record being viewed, when the route encodes one. */
  entity?: string;
}

// Routes that carry a selected entity id, so Dana has a referent for "approve
// this" / "why is this disabled?".
const ENTITY_ROUTES: [RegExp, (id: string) => string][] = [
  [/^employees\/([^/]+)/, (id) => `employee ${id}`],
  [/^payroll\/runs\/([^/]+)/, (id) => `payroll run ${id}`],
  [/^payroll\/loans\/([^/]+)/, (id) => `loan/advance ${id}`],
];

/** Reduce a full pathname to the app-relative path + a friendly page label
 *  (and the selected entity, if the route encodes one). */
export function deriveContext(pathname: string): DerivedContext {
  let rel = pathname;
  const appIdx = rel.indexOf('/app/');
  if (appIdx >= 0) rel = rel.slice(appIdx + 5);
  else {
    const m = rel.match(/^\/(?:t|dashboard)\/[^/]+\/?(.*)$/);
    if (m) rel = m[1];
  }
  rel = rel.replace(/^\/+/, '');
  const label = PAGE_LABELS.find(([p]) => rel === p || rel.startsWith(p + '/'))?.[1];
  let entity: string | undefined;
  for (const [re, fmt] of ENTITY_ROUTES) {
    const m = rel.match(re);
    if (m && m[1] && !['runs', 'loans'].includes(m[1])) {
      entity = fmt(m[1]);
      break;
    }
  }
  return { path: rel || 'dashboard', page: label || 'Dashboard', entity };
}

export interface QuickAction {
  label: string;
  /** Path suffix under the tenant app base, e.g. "time-off". */
  to: string;
  /** Roles allowed to see this action (omit = everyone). */
  roles?: ('admin' | 'manager' | 'employee')[];
}

// Deep-link suggestions per page — frontend-owned (reliable; the LLM never
// invents links). Keyed by the page label from deriveContext. These render as
// one-click action buttons in the assistant, realising "reduce clicks / guide
// directly to the right place".
const QUICK_ACTIONS: Record<string, QuickAction[]> = {
  Dashboard: [
    { label: 'Request leave', to: 'time-off' },
    { label: 'My pay', to: 'my-pay' },
    { label: 'Set up company', to: 'admin/setup', roles: ['admin'] },
  ],
  'Time Off': [{ label: 'Request leave', to: 'time-off' }],
  'My Pay': [{ label: 'View payslips', to: 'my-pay' }],
  'Employee Directory': [
    { label: 'Org chart', to: 'employees' },
    { label: 'Import employees', to: 'admin/import', roles: ['admin'] },
  ],
  'Leave Approvals': [{ label: 'Open approvals', to: 'leave-approvals', roles: ['admin', 'manager'] }],
  'My Team': [{ label: 'Team reports', to: 'team-reports', roles: ['admin', 'manager'] }],
  Administration: [
    { label: 'Set up company', to: 'admin/setup', roles: ['admin'] },
    { label: 'Import employees', to: 'admin/import', roles: ['admin'] },
    { label: 'Departments', to: 'admin/departments', roles: ['admin'] },
    { label: 'Job titles', to: 'admin/job-titles', roles: ['admin'] },
    { label: 'AI Assistant', to: 'admin/ai-assistant', roles: ['admin'] },
  ],
  Departments: [{ label: 'Job titles', to: 'admin/job-titles', roles: ['admin'] }],
  'Job Titles': [{ label: 'Departments', to: 'admin/departments', roles: ['admin'] }],
  'Set up your company': [{ label: 'Open checklist', to: 'admin/setup', roles: ['admin'] }],
  'Company Settings': [{ label: 'AI Assistant', to: 'admin/ai-assistant', roles: ['admin'] }],
  'Payroll Runs': [{ label: 'Bulk salaries', to: 'payroll/salaries', roles: ['admin'] }],
  'Bulk Salaries': [{ label: 'Download roster', to: 'payroll/salaries', roles: ['admin'] }],
};

/** Context-relevant deep-link actions for the current page + role. */
export function quickActionsFor(page: string, role: 'admin' | 'manager' | 'employee'): QuickAction[] {
  return (QUICK_ACTIONS[page] || []).filter((a) => !a.roles || a.roles.includes(role));
}
